import {
  ProtocolKey,
  ProtocolKeyService,
  ProtocolStateService,
  StaticMarket,
} from "@silo/types";
import { Array, Effect, pipe } from "effect";
import { BlobClient } from "../blob-client";
import { getProtocolConfig } from "@silo/config";
import { ContractsClient } from "../contracts-client";
import { getFallbackDisplayMarket } from "./get-fallback-display-market";
import { getProtocolState } from "../protocol/get-protocol-state";
import { KvClient } from "../kv-client";
import { FallbackStrategy } from "./types";
import { match } from "ts-pattern";

export type GetFallbackProtocolMarkets = ReturnType<
  typeof getFallbackProtocolMarkets
>;
export type GetFallbackProtocolMarketsSuccess =
  Effect.Effect.Success<GetFallbackProtocolMarkets>;
export type GetFallbackProtocolMarketsError =
  Effect.Effect.Error<GetFallbackProtocolMarkets>;
export type GetFallbackProtocolMarketsContext =
  Effect.Effect.Context<GetFallbackProtocolMarkets>;

export function getFallbackProtocolMarkets(
  protocolKey: ProtocolKey,
  strategy: FallbackStrategy = "kv"
) {
  return Effect.gen(function* () {
    const [staticMarkets, protocolState] = yield* pipe(
      Effect.all(
        [BlobClient.getProtocolStaticMarkets(protocolKey), getProtocolState()],
        { concurrency: "unbounded" }
      ),
      Effect.provideService(ProtocolKeyService, protocolKey)
    );

    const protocolConfig = getProtocolConfig(protocolKey);

    const displayStaticMarkets = pipe(
      staticMarkets,
      Array.filter((market) =>
        protocolConfig.marketWhitelist.has(market.marketAddress)
      )
    );

    const displayMarketArray = yield* pipe(
      match(strategy)
        .with("kv", () =>
          Effect.firstSuccessOf([
            getKvDisplayMarketArray(displayStaticMarkets),
            getStaticDisplayMarketArray(displayStaticMarkets),
          ])
        )
        .with("static", () => getStaticDisplayMarketArray(displayStaticMarkets))
        .exhaustive(),
      Effect.provideService(ProtocolKeyService, protocolKey),
      Effect.provideService(ProtocolStateService, protocolState)
    );

    return displayMarketArray;
  });
}

export type GetKvDisplayMarketArray = ReturnType<
  typeof getKvDisplayMarketArray
>;
export type GetKvDisplayMarketArraySuccess =
  Effect.Effect.Success<GetKvDisplayMarketArray>;
export type GetKvDisplayMarketArrayError =
  Effect.Effect.Error<GetKvDisplayMarketArray>;
export type GetKvDisplayMarketArrayContext =
  Effect.Effect.Context<GetKvDisplayMarketArray>;

export function getKvDisplayMarketArray(staticMarkets: StaticMarket[]) {
  return Effect.gen(function* () {
    const marketMetaArray = yield* KvClient.getMarketMetaArray(staticMarkets);

    const displayMarketArray = yield* Effect.all(
      marketMetaArray.map((marketMeta) => getFallbackDisplayMarket(marketMeta)),
      { concurrency: "unbounded" }
    );

    const protocolKey = yield* ProtocolKeyService;

    yield* Effect.log(
      `Complete ✅: get ${protocolKey} markets with kv strategy`
    );

    return displayMarketArray;
  });
}

export type GetStaticDisplayMarketArray = ReturnType<
  typeof getStaticDisplayMarketArray
>;
export type GetStaticDisplayMarketArraySuccess =
  Effect.Effect.Success<GetStaticDisplayMarketArray>;
export type GetStaticDisplayMarketArrayError =
  Effect.Effect.Error<GetStaticDisplayMarketArray>;
export type GetStaticDisplayMarketArrayContext =
  Effect.Effect.Context<GetStaticDisplayMarketArray>;

export function getStaticDisplayMarketArray(staticMarkets: StaticMarket[]) {
  return Effect.gen(function* () {
    const displayMarketArray = yield* Effect.all(
      staticMarkets.map((staticMarket) =>
        pipe(
          ContractsClient.getMarketMeta(staticMarket),
          Effect.andThen((marketMeta) => getFallbackDisplayMarket(marketMeta))
        )
      ),
      { concurrency: "unbounded" }
    );

    const protocolKey = yield* ProtocolKeyService;

    yield* Effect.log(
      `Complete ✅: get ${protocolKey} markets with static strategy`
    );

    return displayMarketArray;
  });
}
